
import { Injectable } from "@angular/core";
import { signInWithRedirect, signOut, fetchAuthSession } from "aws-amplify/auth";

export type UserType = 'admin' | 'tenant';

declare const logout_redirect_uri: string;
declare const cognitoDomain: string;
declare const userPoolClientId: string;

@Injectable({
  providedIn: 'root'
})

export class AuthService {
    private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
    private tabId: string | null = null;
    private listenersAdded = false;
    private lastThrottleWrite = 0;

    private readonly TAB_KEY_PREFIX = 'tab_';

    // User is logged out after 15 minutes of no interaction across all tabs
    private readonly INACTIVITY_TIMEOUT = 15 * 60 * 1000;

    // Throttle for writing user activity timestamp to localStorage
    private readonly ACTIVITY_THROTTLE = 5 * 1000;

    // Bound references for proper add/removeEventListener
    private readonly boundOnActivity = this.onActivity.bind(this);
    private readonly boundOnVisibilityChange = this.onVisibilityChange.bind(this);
    private readonly boundOnStorageChange = this.onStorageChange.bind(this);
    private readonly boundOnPageHide = this.onPageHide.bind(this);

    constructor() {}

    async login(): Promise<'redirected' | 'already-authenticated'> {
        // Cleared before signInWithRedirect: code after it races against the page unload
        localStorage.removeItem('logoutEvent');

        try {
            await signInWithRedirect();
        } catch (error) {
            // Amplify tokens are in localStorage, shared across tabs — a login in another
            // tab makes signInWithRedirect throw UserAlreadyAuthenticatedException here
            if (await this.isAuthenticated()) {
                return 'already-authenticated';
            }
            // Stale local session (e.g. expired refresh token) also blocks the redirect —
            // clear it and retry
            await signOut();
            await signInWithRedirect();
        }
        return 'redirected';
    }

    async logout() {
        this.stopMonitoring();
        this.removeTabKey();
        localStorage.removeItem('lastActiveTimestamp');
        sessionStorage.removeItem('tabId');
        await signOut({ global: true });
    }

    async logoutUserNotFound() {
        this.stopMonitoring();
        this.removeTabKey();
        localStorage.removeItem('lastActiveTimestamp');
        await signOut({ global: true });
        localStorage.setItem('logoutEvent', 'userNotFound');
        window.location.href = `https://${cognitoDomain}/logout?client_id=${userPoolClientId}&logout_uri=${logout_redirect_uri}`;
    }

    async getUser() {
        try {
            const session = await fetchAuthSession();
            const payload = session.tokens?.idToken?.payload ?? {};

            const cognitoGroups = payload["cognito:groups"] as string[] || [];
            const userType: UserType = cognitoGroups.includes('admin') ? 'admin' : 'tenant';

            return {
                username: payload["cognito:username"],
                email: payload["email"],
                name: payload["name"],
                userType: userType
            }
        } catch (error) {
            return null;
        }
    }

    async isAuthenticated() {
        try {
            const session = await fetchAuthSession();
            return session.tokens?.idToken != null;
        } catch (error) {
            return false;
        }
    }

    async getToken(): Promise<string | null> {
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString() ?? null;
            return token;
        } catch (error) {
            return null;
        }
    }

    // --- Tab lifecycle & monitoring ---

    // Returns false if a tab-close logout was triggered (caller should not render the app)
    async startMonitoring(): Promise<boolean> {
        const existingTabId = sessionStorage.getItem('tabId');

        if (existingTabId) {
            // Page refresh — re-register the same tab and continue
            this.tabId = existingTabId;
            this.registerTab();
        } else {
            // Fresh tab — check if all previous tabs are gone (closed/crashed)
            if (this.hasTabKeys() === false && localStorage.getItem('lastActiveTimestamp')) {
                await this.performLogout('tabClose');
                return false;
            }
            this.tabId = crypto.randomUUID();
            sessionStorage.setItem('tabId', this.tabId);
            this.registerTab();
        }

        // Listen for tab close to remove this tab's key
        window.addEventListener('pagehide', this.boundOnPageHide);

        if (!this.listenersAdded) {
            this.addActivityListeners();
            this.addVisibilityChangeListener();
            this.addStorageListener();
            this.listenersAdded = true;
        }
        this.updateLastActiveTimestamp();
        this.resetInactivityTimer();

        return true;
    }

    stopMonitoring() {
        this.clearInactivityTimer();
        window.removeEventListener('pagehide', this.boundOnPageHide);

        this.removeActivityListeners();
        this.removeVisibilityChangeListener();
        this.removeStorageListener();
        this.listenersAdded = false;
    }

    // --- Inactivity timer ---

    private resetInactivityTimer() {
        this.clearInactivityTimer();
        const delay = this.getRemainingInactivityTime();
        this.inactivityTimer = setTimeout(() => {
            if (this.shouldLogoutForInactivity()) {
                this.performLogout('inactivity');
            } else {
                // Another tab was active — reschedule for remaining time
                this.resetInactivityTimer();
            }
        }, delay);
    }

    private getRemainingInactivityTime(): number {
        const lastActiveStr = localStorage.getItem('lastActiveTimestamp');
        if (!lastActiveStr) return this.INACTIVITY_TIMEOUT;
        const elapsed = Date.now() - parseInt(lastActiveStr, 10);
        return Math.max(this.INACTIVITY_TIMEOUT - elapsed, 1000);
    }

    private clearInactivityTimer() {
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer);
            this.inactivityTimer = null;
        }
    }

    private shouldLogoutForInactivity(): boolean {
        const lastActiveStr = localStorage.getItem('lastActiveTimestamp');
        // Missing timestamp means another logout flow already cleared it — not inactivity
        if (!lastActiveStr) return false;
        return Date.now() - parseInt(lastActiveStr, 10) >= this.INACTIVITY_TIMEOUT;
    }

    // --- Tab tracking ---

    private registerTab() {
        if (this.tabId) {
            localStorage.setItem(this.TAB_KEY_PREFIX + this.tabId, '1');
        }
    }

    private removeTabKey() {
        if (this.tabId) {
            localStorage.removeItem(this.TAB_KEY_PREFIX + this.tabId);
            this.tabId = null;
        }
    }

    private hasTabKeys(): boolean {
        for (let i = 0; i < localStorage.length; i++) {
            if (localStorage.key(i)?.startsWith(this.TAB_KEY_PREFIX)) {
                return true;
            }
        }
        return false;
    }

    // pagehide fires reliably on tab/browser close (unlike beforeunload/unload)
    private onPageHide() {
        this.removeTabKey();
    }

    // --- Logout ---

    private async performLogout(reason: 'inactivity' | 'tabClose') {
        await this.logout();
        localStorage.setItem('logoutEvent', reason);
        window.location.href = `https://${cognitoDomain}/logout?client_id=${userPoolClientId}&logout_uri=${logout_redirect_uri}`;
    }

    // --- Activity tracking ---

    private onActivity() {
        this.resetInactivityTimer();
        const now = Date.now();
        if (now - this.lastThrottleWrite >= this.ACTIVITY_THROTTLE) {
            this.updateLastActiveTimestamp();
            this.lastThrottleWrite = now;
        }
    }

    private updateLastActiveTimestamp() {
        localStorage.setItem('lastActiveTimestamp', Date.now().toString());
    }

    private addActivityListeners() {
        ['click', 'keydown', 'scroll', 'mousemove'].forEach(event => {
            document.addEventListener(event, this.boundOnActivity, true);
        });
    }

    private removeActivityListeners() {
        ['click', 'keydown', 'scroll', 'mousemove'].forEach(event => {
            document.removeEventListener(event, this.boundOnActivity, true);
        });
    }

    // --- Visibility change (replaces focus listener) ---

    private addVisibilityChangeListener() {
        document.addEventListener('visibilitychange', this.boundOnVisibilityChange);
    }

    private removeVisibilityChangeListener() {
        document.removeEventListener('visibilitychange', this.boundOnVisibilityChange);
    }

    private async onVisibilityChange() {
        if (document.visibilityState !== 'visible') return;

        if (this.shouldLogoutForInactivity()) {
            this.performLogout('inactivity');
            return;
        }

        const isAuth = await this.isAuthenticated();
        if (!isAuth) {
            window.location.reload();
        }
    }

    // --- Cross-tab storage listener ---

    private addStorageListener() {
        window.addEventListener('storage', this.boundOnStorageChange);
    }

    private removeStorageListener() {
        window.removeEventListener('storage', this.boundOnStorageChange);
    }

    private onStorageChange(e: StorageEvent) {
        // Any logout in another tab (manual, inactivity, tab close, user not found):
        // reload so this tab lands on the login page instead of lingering logged-out
        if (e.key === 'logoutEvent' && e.newValue) {
            window.location.reload();
        }
    }
}
